import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Container, Modal, Button } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import { trackGaEvent } from '../lib/ga';

const TRIAL_URL =
  process.env.REACT_APP_SCALE2REV_TRIAL_URL ||
  process.env.REACT_APP_SCALE2REV_SIGNUP_URL ||
  'https://scale2rev.com/trial/engage/first-to-answer';
const PRICING_URL =
  process.env.REACT_APP_SCALE2REV_PRICING_URL || 'https://scale2rev.com/pricing';
const COUNTDOWN_SECONDS = 5;

function resolveSignupRedirect(searchParams) {
  if (searchParams.get('intent') === 'pricing') {
    return { url: PRICING_URL, destination: 'scale2rev_pricing', intent: 'pricing' };
  }
  return { url: TRIAL_URL, destination: 'scale2rev_trial', intent: 'trial' };
}

function SignupRedirect() {
  const [searchParams] = useSearchParams();
  const { url: redirectUrl, destination, intent } = useMemo(
    () => resolveSignupRedirect(searchParams),
    [searchParams]
  );
  const [secondsLeft, setSecondsLeft] = useState(COUNTDOWN_SECONDS);
  const redirectedRef = useRef(false);

  const redirect = useCallback(() => {
    if (redirectedRef.current) return;
    redirectedRef.current = true;
    trackGaEvent('signup_redirect', { destination, intent, page_path: '/signup' });
    window.location.assign(redirectUrl);
  }, [destination, intent, redirectUrl]);

  useEffect(() => {
    document.title = 'Sign Up — Redirecting to Scale2Rev Engage | TheSkinnyAI';
    trackGaEvent('signup_redirect_view', { intent, page_path: '/signup' });
  }, [intent]);

  useEffect(() => {
    if (secondsLeft <= 0) {
      redirect();
      return undefined;
    }
    const timer = window.setTimeout(() => {
      setSecondsLeft((prev) => prev - 1);
    }, 1000);
    return () => window.clearTimeout(timer);
  }, [secondsLeft, redirect]);

  const bodyCopy = intent === 'pricing'
    ? 'You will be taken to Scale2Rev pricing to choose a plan in'
    : 'You will be redirected to start your free trial in';

  return (
    <Container className="py-5">
      <Modal show centered backdrop="static" keyboard={false} data-bs-theme="light">
        <Modal.Header>
          <Modal.Title>TheSkinnyAI is now Scale2Rev Engage</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0">
            {bodyCopy}{' '}
            <strong>{secondsLeft}</strong> {secondsLeft === 1 ? 'second' : 'seconds'}.
          </p>
        </Modal.Body>
        <Modal.Footer className="d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center justify-content-between gap-2">
          <Button variant="primary" onClick={redirect}>
            Continue now
          </Button>
          <a
            href="/login"
            className="text-muted text-center text-sm-end"
            style={{ fontSize: '0.875rem' }}
          >
            Already have an account? Log in to TheSkinnyAI
          </a>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default SignupRedirect;
